import { SwapHoriz } from '@mui/icons-material';
import { Dialog, DialogActions } from '@mui/material';
import Grid from '@mui/material/Grid';
import { UsersResponse } from 'lib/api/portfolio/types';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { postTransferPortfolio } from '../../../api/portfolio';
import CustomButton from '../../../designSystem/Button';
import { showAlert } from '../../../functions/utils';
import WalletList from './WalletList';

type UserId = number | undefined;

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  options: UsersResponse;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  userToTransfer: number | undefined;
}

const Commercial = (props: Props) => {
  const dispatch = useDispatch();
  const { open, setOpen, options, loading, setLoading, userToTransfer } = props;
  const [sourceUserId, setSourceUserId] = useState<UserId>(undefined);
  const [destinationUserId, setDestinationUserId] = useState<UserId>(undefined);
  const [movedBusinessIds, setMovedBusinessIds] = useState<number[]>([]);

  const sourceUser = options.find((option) => option.id === sourceUserId);
  const destinationUser = options.find(
    (option) => option.id === destinationUserId,
  );

  const originPortfolio =
    sourceUser?.commercialPortfolio.filter(
      (b) => !movedBusinessIds.includes(b.id),
    ) ?? [];

  const movedFromSource =
    sourceUser?.commercialPortfolio.filter((b) =>
      movedBusinessIds.includes(b.id),
    ) ?? [];

  const destinationBasePortfolio = destinationUser?.commercialPortfolio ?? [];

  const newTransferredFromSource = movedFromSource.filter(
    (b) => !destinationBasePortfolio.some((db) => db.id === b.id),
  );

  const overlappingTransferred = destinationBasePortfolio.filter((b) =>
    movedBusinessIds.includes(b.id),
  );

  const destinationPortfolio = [
    ...destinationBasePortfolio,
    ...newTransferredFromSource,
  ];

  const destinationOptions = options.filter(
    (option) => option.id !== sourceUserId,
  );

  const handleToggleFromSource = (id: number) => {
    if (!sourceUser) return;
    if (!sourceUser.commercialPortfolio.some((b) => b.id === id)) return;

    setMovedBusinessIds((prev) =>
      prev.includes(id)
        ? prev.filter((businessId) => businessId !== id)
        : [...prev, id],
    );
  };

  const handleAllRight = () => {
    if (!sourceUser) return;
    const allOriginIds = sourceUser.commercialPortfolio.map((item) => item.id);
    if (!allOriginIds.length) return;

    setMovedBusinessIds((prev) =>
      Array.from(new Set([...prev, ...allOriginIds])),
    );
  };

  const handleSelectOriginUser = (value: number) => {
    setSourceUserId(value);
    setDestinationUserId(undefined);
    setMovedBusinessIds([]);
  };

  const handleSelectDestinationUser = (value: number) => {
    setDestinationUserId(value);
    setMovedBusinessIds([]);
  };

  const handleUndoFromDestination = (id: number) => {
    if (!movedBusinessIds.includes(id)) return;
    setMovedBusinessIds((prev) =>
      prev.filter((businessId) => businessId !== id),
    );
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      if (!sourceUser || !destinationUser) {
        showAlert(
          dispatch,
          'error',
          'Usuario de origen o destino no seleccionado',
        );
        return;
      }

      if (!movedBusinessIds.length) {
        showAlert(
          dispatch,
          'error',
          'No hay clientes seleccionados para transferir',
        );
        return;
      }

      const payload = {
        transfer: {
          sourceUserId: sourceUser.id,
          userId: destinationUser.id,
          portfolioIds: movedBusinessIds,
        },
      };
      await postTransferPortfolio(dispatch, payload);
      showAlert(dispatch, 'success', 'Cartera transferida con éxito');
      setOpen(false);
    } catch {
      showAlert(dispatch, 'error', 'Error al transferir la cartera');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userToTransfer) {
      handleSelectOriginUser(userToTransfer);
    }
  }, [userToTransfer]);

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle
        onClose={() => setOpen(false)}
        title="Transferir cartera comercial"
        icon={<SwapHoriz color="primary" />}
        color="primary"
      />
      <Grid container spacing={2} mb={2} px={3}>
        <Grid item xs={6}>
          <WalletList
            users={options}
            selectedUserId={sourceUserId}
            onChangeUser={handleSelectOriginUser}
            items={originPortfolio}
            isSource
            onToggleItem={handleToggleFromSource}
            onToggleAll={handleAllRight}
            destinationUser={destinationUser}
          />
        </Grid>
        <Grid item xs={6}>
          <WalletList
            users={destinationOptions}
            selectedUserId={destinationUserId}
            onChangeUser={handleSelectDestinationUser}
            items={destinationPortfolio}
            isSource={false}
            highlightedNewIds={newTransferredFromSource.map((b) => b.id)}
            existingTransferredIds={overlappingTransferred.map((b) => b.id)}
            onToggleItem={handleUndoFromDestination}
          />
        </Grid>
      </Grid>
      <DialogActions>
        <CustomButton color="secondary" onClick={() => setOpen(false)}>
          Cancelar
        </CustomButton>
        <CustomButton
          disabled={loading || !sourceUser || !destinationUser}
          onClick={() => handleSave()}
          loading={loading}
        >
          Guardar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default Commercial;
